import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Edit, FileText, Upload } from 'lucide-react';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { DetailField } from './course/DetailField';
import { useTeachers } from '../../hooks/useTeachers';
import { teachersStore } from '../../services/teachersStore';
import { teacherDocumentKinds } from '../../data/teacherOptions';
import { formatEuro } from '../../utils/currency';
import { productLabel } from '../../utils/trainingCourses';
import { formatTimestamp, teacherCategories, teacherFullName } from '../../utils/teachers';
import { TeacherDocumentKind } from '../../types/teacher';
interface TeacherDetailModalProps {
  teacherId: string | null;
  onClose: () => void;
  onEdit?: (teacherId: string) => void;
}
export function TeacherDetailModal({
  teacherId,
  onClose,
  onEdit
}: TeacherDetailModalProps) {
  const teachers = useTeachers();
  const courses = useTrainingCourses();
  const teacher = teachers.find(t => t.id === teacherId);
  const assignedCourses = teacher ? courses.filter(c => (c.teachers || []).some(ct => ct.teacherId === teacher.id)) : [];
  const inputRefs = useRef<Partial<Record<TeacherDocumentKind, HTMLInputElement | null>>>({});
  const handleUpload = (kind: TeacherDocumentKind, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !teacher) return;
    teachersStore.uploadDocument(teacher.id, kind, file.name);
    e.target.value = '';
  };
  const sectionTitle = 'text-sm font-semibold text-seform-navy mb-4';
  return <Modal isOpen={Boolean(teacher)} onClose={onClose} title="Anagrafica docente" size="xl" data-id="element-1732" data-mp="2oimduakuujuw">
      {teacher && <div className="space-y-6" data-id="element-1733" data-mp="2xj2gjhtrxmyp">
          <div className="flex items-start justify-between gap-4 pb-5 border-b border-slate-100" data-id="element-1734" data-mp="2xju3y5d7wedw">
            <div data-id="element-1735" data-mp="1v6frbi59o81g">
              <h3 className="text-xl font-bold text-seform-navy" data-id="element-1736" data-mp="1v6xqamjbd0d2">
                {teacherFullName(teacher)}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-1737" data-mp="2023bfj4w6b03">
                {teacher.email} · {teacher.phone}
              </p>
            </div>
            <p className="text-right" data-id="element-1738" data-mp="28xt50fghubok">
              <span className="block text-xl font-bold text-seform-navy tabular-nums" data-id="element-1739" data-mp="28yvxgrtgsm6l">
                {formatEuro(teacher.hourlyRate)}
              </span>
              <span className="text-xs text-slate-500" data-id="element-1740" data-mp="2itw7jmf9ccc1">tariffa oraria</span>
            </p>
          </div>

          <section data-id="element-1741" data-mp="2nvd6hce6lopz">
            <h4 className={sectionTitle} data-id="element-1742" data-mp="2nutmlq4z0q9b">Dati anagrafici</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1743" data-mp="2rvx4ei6kwwd6">
              <DetailField label="Nome" data-id="element-1744" data-mp="tgajblcluiq8">{teacher.firstName}</DetailField>
              <DetailField label="Cognome" data-id="element-1745" data-mp="ti3vth9wfx65">{teacher.lastName}</DetailField>
              <DetailField label="CF" data-id="element-1746" data-mp="xi4hnmzbvan8">
                <span className="font-mono" data-id="element-1747" data-mp="py3dum8ynk9h">{teacher.fiscalCode}</span>
              </DetailField>
              <DetailField label="Email" data-id="element-1748" data-mp="l7d7b5ostbd7">{teacher.email}</DetailField>
              <DetailField label="Telefono" data-id="element-1749" data-mp="l625vcitsfp9">{teacher.phone}</DetailField>
              <DetailField label="Zone coperte" data-id="element-1750" data-mp="g9bmaf7tb1z3">{teacher.zones}</DetailField>
              <DetailField label="Disponibilità limitata" full data-id="element-1751" data-mp="c789nn9eprc8">
                {teacher.limitedAvailability}
              </DetailField>
            </dl>
          </section>

          <section className="pt-6 border-t border-slate-100" data-id="element-1752" data-mp="13xuy34q2lwvx">
            <h4 className={sectionTitle} data-id="element-1753" data-mp="13ye3jlyjast2">Corsi erogabili</h4>
            <ul className="space-y-1.5 mb-4" data-id="element-1754" data-mp="zvrh60kmu039">
              {teacher.productCodes.map(code => <li key={code} className="text-sm text-slate-700" data-id="element-1755" data-mp="uxzh2j9u37nz">
                  {productLabel(code)}
                </li>)}
            </ul>
            <p className="text-xs font-medium text-slate-500 mb-2" data-id="element-1756" data-mp="3kk5i3ky6exr4">
              Macrocategorie
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-1757" data-mp="17epgiio1i9ca">
              {teacherCategories(teacher).map(c => <Badge key={c} variant="info" size="sm" data-id="element-1758" data-mp="1c55tk1ny7sy4">
                  {c}
                </Badge>)}
            </div>
          </section>

          <section className="pt-6 border-t border-slate-100" data-id="element-1759" data-mp="yfvm8yy6lv1w">
            <h4 className={sectionTitle} data-id="element-1760" data-mp="12euqehc39nsa">Corsi assegnati</h4>
            {assignedCourses.length === 0 ? <p className="text-sm text-slate-500" data-id="element-1761" data-mp="1q69vghv92j78">Nessun corso assegnato.</p> : <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-id="element-1762" data-mp="1v38osy73am4d">
                {assignedCourses.map(c => {
            const row = c.teachers?.find(ct => ct.teacherId === teacher.id);
            return <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-1763" data-mp="1m5g41eilmm9l">
                      <Link to={`/training/corsi/${c.id}`} onClick={onClose} className="min-w-0 text-sm font-medium text-seform-navy hover:text-seform-orange truncate" data-id="element-1764" data-mp="aogfmtzdgrku">
                        <span className="font-mono text-xs text-slate-500 mr-2" data-id="element-1765" data-mp="3uap0jvwx344z">{c.courseCode}</span>
                        {c.name}
                      </Link>
                      {row && <Badge variant={row.assignmentStatus === 'Confermato' ? 'success' : row.assignmentStatus === 'Inviato' ? 'info' : 'warning'} size="sm" data-id="element-1766" data-mp="7724fwdi2q5c">
                          {row.assignmentStatus}
                        </Badge>}
                    </li>;
          })}
              </ul>}
          </section>

          <section className="pt-6 border-t border-slate-100" data-id="element-1767" data-mp="2rux6xjgzzs5k">
            <h4 className={sectionTitle} data-id="element-1768" data-mp="vt3m0bepwxqc">Condizioni</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1769" data-mp="10sg5qnaaloop">
              <DetailField label="Tariffa oraria" data-id="element-1770" data-mp="10qdbfr23x9e6">
                {formatEuro(teacher.hourlyRate)}
              </DetailField>
              <DetailField label="Metodo di fatturazione" data-id="element-1771" data-mp="19ljguoxgtpg6">
                {teacher.billingMethod}
              </DetailField>
              <DetailField label="Accordi" data-id="element-1772" data-mp="cw2dwhpvahi6">{teacher.agreements}</DetailField>
              <DetailField label="Rimborso spese" data-id="element-1773" data-mp="ht2b034qo9x5">
                {teacher.expenseReimbursement}
              </DetailField>
              <DetailField label="Attrezzatura privata disponibile" full data-id="element-1774" data-mp="ltvxqt6g3iog">
                {teacher.privateEquipment.length > 0 && <span className="flex flex-wrap gap-2 mt-0.5" data-id="element-1775" data-mp="qs5w1cnguzea">
                    {teacher.privateEquipment.map(item => <Badge key={item} variant="neutral" size="sm" data-id="element-1776" data-mp="2295u6owhw8g8">
                        {item}
                      </Badge>)}
                  </span>}
              </DetailField>
            </dl>
          </section>

          <section className="pt-6 border-t border-slate-100" data-id="element-1777" data-mp="27gm3l697qkse">
            <h4 className={sectionTitle} data-id="element-1778" data-mp="22mretoer3fmg">Documentazione docente</h4>
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-id="element-1779" data-mp="1ymhaeg6lwx9p">
              {teacherDocumentKinds.map(({
            kind,
            label
          }) => {
            const doc = teacher.documents[kind];
            return <li key={kind} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3" data-id="element-1780" data-mp="1on9w9qs1sylj">
                    <div className="flex items-start gap-3 min-w-0" data-id="element-1781" data-mp="1fo4y4lkef79w">
                      {doc ? <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1782" data-mp="3e397ddf88y3t" /> : <FileText className="h-5 w-5 text-slate-300 flex-shrink-0 mt-0.5" data-id="element-1783" data-mp="3a1w1j3c4wuww" />}
                      <div className="min-w-0" data-id="element-1784" data-mp="3tc8r9y8m0vbu">
                        <p className="text-sm font-medium text-slate-800" data-id="element-1785" data-mp="16lbyld5c4h2">
                          {label}
                        </p>
                        <p className="text-xs text-slate-500 truncate" data-id="element-1786" data-mp="3jjt66s20nqmq">
                          {doc ? `${doc.fileName} · caricato il ${formatTimestamp(doc.uploadedAt)}` : 'Non caricato'}
                        </p>
                      </div>
                    </div>
                    <input ref={el => {
                inputRefs.current[kind] = el;
              }} type="file" className="hidden" onChange={e => handleUpload(kind, e)} data-id="element-1787" data-mp="3eivi5yubs2db" />
                    <Button variant="flat" color="secondary" size="sm" startContent={<Upload className="h-4 w-4" data-id="element-1789" data-mp="1dxwvzyp85332" />} onClick={() => inputRefs.current[kind]?.click()} data-id="element-1788" data-mp="ys9b3e0zl0wa">
                      {doc ? 'Sostituisci' : 'Upload'}
                    </Button>
                  </li>;
          })}
            </ul>
          </section>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1790" data-mp="1hxfgitd4vc5d">
            <Button variant="flat" color="default" onClick={onClose} data-id="element-1791" data-mp="1wmbkae85q306">
              Chiudi
            </Button>
            {onEdit && <Button startContent={<Edit className="h-4 w-4" data-id="element-1793" data-mp="25okut43fdwdk" />} onClick={() => onEdit(teacher.id)} data-id="element-1792" data-mp="1rqw8rsxmgj6x">
                Modifica
              </Button>}
          </div>
        </div>}
    </Modal>;
}